import "@/App.css";
import axios from "axios";
import { Menu, RefreshCw, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import DataTableTab from "./components/DataTableTab";
import KPICard from "./components/KPICard";
import PATLineChart from "./components/PATLineChart";
import RevenueEBITDAChart from "./components/RevenueEBITDAChart";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AOP_ADVANCED_URL = "https://aop-pro.slicearrow.com/";

const tabs = [
  "Setup",
  "Benchmarks",
  "Assumptions",
  "Scenarios",
  "Revenue",
  "COGS_Opex",
  "Capex_Finance",
  "P&L",
  "Cash_Flow",
  "Dashboard",
];

function App() {
  const [activeTab, setActiveTab] = useState("Dashboard");
  const [loading, setLoading] = useState(true);
  const [tabData, setTabData] = useState({});
  const [chartData, setChartData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // useEffect(() => {
  //   fetchAllData();
  //   // Poll for changes every 10 seconds
  //   const interval = setInterval(checkForChanges, 10000);
  //   return () => clearInterval(interval);
  // }, []);
  useEffect(() => {
    // initial load only — no polling or auto-refresh
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Fetch chart data from graphs sheet
      const chartResponse = await axios.get(`${API}/sheets/data`);
      setChartData(chartResponse.data);

      // Fetch settings
      const settingsResponse = await axios.get(`${API}/sheets/settings`);
      setSettings(settingsResponse.data);

      // Fetch data for all tabs
      const tabPromises = tabs
        .filter((t) => t !== "Dashboard")
        .map(async (tab) => {
          const response = await axios.get(`${API}/sheets/tab/${tab}`);
          return { tab, data: response.data.data };
        });

      const tabResults = await Promise.all(tabPromises);
      const newTabData = {};
      tabResults.forEach(({ tab, data }) => {
        newTabData[tab] = data;
      });
      setTabData(newTabData);

      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  const checkForChanges = async () => {
    try {
      const response = await axios.get(`${API}/sheets/settings/changes`);
      if (response.data.changed) {
        fetchAllData();
      }
    } catch (error) {
      console.error("Error checking for changes:", error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
  };

  const handleDataUpdate = async () => {
    // When child indicates data changed, refresh data and show confirmation after refresh completes
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
    toast.success("Cell Updated Successfully");
  };

  const getChartDataFormatted = () => {
    if (!chartData) return [];

    return chartData.periods.map((period, idx) => ({
      period,
      Revenue: chartData.revenue[idx],
      EBITDA: chartData.ebitda[idx],
      PAT: chartData.pat[idx],
    }));
  };

  const isTabEditable = (tab) => {
    return ["Setup", "Benchmarks", "Assumptions", "Scenarios"].includes(tab);
  };

  const renderContent = () => {
    if (activeTab === "Dashboard") {
      return (
        <div className="space-y-6">
          <div className="flex items-center justify-between mt-4">
            <h1 className="text-3xl font-bold text-gray-900">
              Dashboard Overview
            </h1>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
              data-testid="refresh-button"
            >
              <RefreshCw
                className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>

          {/* Charts - Enlarged View */}
          <div className="space-y-6">
            <RevenueEBITDAChart
              data={getChartDataFormatted()}
              loading={loading}
            />
            <PATLineChart data={getChartDataFormatted()} loading={loading} />
          </div>
        </div>
      );
    }

    return (
      <DataTableTab
        data={tabData[activeTab]}
        tabName={activeTab}
        sheetName={activeTab}
        editable={isTabEditable(activeTab)}
        onDataUpdate={handleDataUpdate}
        settings={settings}
      />
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Toaster position="top-right" richColors />

      {/* Header with KPIs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
                data-testid="sidebar-toggle"
              >
                {sidebarOpen ? (
                  <X className="w-5 h-5" />
                ) : (
                  <Menu className="w-5 h-5" />
                )}
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  AOP Scenario Analysis
                </h1>
                <p className="text-sm text-gray-500">
                  Annual Operating Plan Builder
                </p>
              </div>
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
              data-testid="header-refresh-button"
            >
              <RefreshCw
                className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
              />
              Refresh Data
            </button>
          </div>

          {/* KPIs Row
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <KPICard
              title="Total Revenue"
              value={chartData?.revenue_total || 0}
              currency="₹"
              loading={loading}
            />
            <KPICard
              title="Total EBITDA"
              value={chartData?.ebitda_total || 0}
              currency="₹"
              loading={loading}
            />
            <KPICard
              title="Total PAT"
              value={chartData?.pat_total || 0}
              currency="₹"
              loading={loading}
            />
          </div> */}
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex">
        {/* Sidebar - fixed on the left; hidden on small screens via translate-x; always visible on lg */}
        <div
          className={`fixed left-0 top-18 bg-white border-r border-gray-200 transition-transform duration-300 z-30 w-48 ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0`}
          style={{ height: "calc(100vh - 4rem)" }}
        >
          <div className="p-4">
            <h2 className="text-xs font-semibold text-gray-400 uppercase mb-2">
              Tabs
            </h2>
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    if (window.innerWidth < 1024) setSidebarOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                    activeTab === tab
                      ? "bg-indigo-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  data-testid={`tab-${tab.toLowerCase()}`}
                >
                  {tab.replace("_", " & ")}
                </button>
              ))}
            </nav>

            <div className="mt-4">
              <button
                onClick={() =>
                  window.open(AOP_ADVANCED_URL, "_blank", "noopener")
                }
                className="w-full px-4 py-2 rounded-lg text-white font-semibold bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 hover:opacity-95 transition-shadow shadow-md"
                data-testid="aop-advanced-button"
              >
                AOP Advanced
              </button>
            </div>
          </div>
        </div>

        {/* Main Content Area - offset on large screens to account for fixed sidebar */}
        <div className="flex-1 flex min-w-0 lg:ml-48">
          {/* Middle Content */}
          <div
            className={`flex-1 p-6 min-w-0 ${
              activeTab !== "Dashboard" ? "lg:pr-[33vw]" : ""
            }`}
          >
            {/* KPIs Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mr-4">
              <KPICard
                title="Total Revenue"
                value={chartData?.revenue_total || 0}
                currency="₹"
                loading={loading}
              />
              <KPICard
                title="Total EBITDA"
                value={chartData?.ebitda_total || 0}
                currency="₹"
                loading={loading}
              />
              <KPICard
                title="Total PAT"
                value={chartData?.pat_total || 0}
                currency="₹"
                loading={loading}
              />
              <KPICard
                title="Total PAT"
                value={chartData?.pat_total || 0}
                currency="₹"
                loading={loading}
              />
            </div>

            {/* Scrollable content wrapper: only this section will scroll when its content overflows */}
            <div className="w-full overflow-auto">{renderContent()}</div>
          </div>

          {/* Right Sidebar - Mini Charts (visible on non-Dashboard tabs) */}
          {activeTab !== "Dashboard" && (
            <div
              className="hidden lg:block w-[33vw] fixed right-0 top-18 bg-white border-l border-gray-200 p-4"
              style={{ height: "calc(100vh - 4rem)" }}
            >
              {/* Fixed Quick View header */}
              <div className="sticky top-0 z-30 bg-white h-14 flex items-center">
                <h2 className="text-sm font-semibold text-gray-700">
                  Quick View
                </h2>
              </div>

              {/* Scrollable charts container below the header */}
              <div
                className="space-y-4 pt-2 overflow-y-auto"
                style={{ height: "calc(100% - 56px)" }}
              >
                <div className="transform scale-90 origin-top">
                  <RevenueEBITDAChart
                    data={getChartDataFormatted()}
                    loading={loading}
                  />
                </div>
                <div className="transform scale-90 origin-top">
                  <PATLineChart
                    data={getChartDataFormatted()}
                    loading={loading}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
